/**
 * Helper for static-text + static-image widgets. Converts the field bag
 * (idleAnimation / entranceAnimation / animationSpeed / animationIntensity /
 *  glowColor / shakeIntervalSeconds) into a className + style payload that
 * the CSS in `styles/animations.css` consumes.
 *
 * Pure CSS animations — no JS loops. Exit animations are accepted by the
 * schema but don't fire on unmount in this revision (the widget just
 * disappears; React doesn't keep it mounted long enough to play out).
 */

export type IdleAnimation =
  | 'none'
  // text + image
  | 'shake'
  | 'wobble'
  | 'bounce'
  | 'pulse'
  | 'glitch'
  // text only
  | 'flicker'
  | 'rainbow'
  | 'typewriter'
  | 'wave'
  // image only
  | 'float'
  | 'spin'
  | 'spin-slow'
  | 'neon-glow'
  | 'shake-interval';

export type EntranceAnimation =
  | 'none'
  | 'fade-in'
  | 'slide-left'
  | 'slide-right'
  | 'slide-down'
  | 'pop'
  | 'typewriter'
  | 'zoom-in'
  | 'rotate-in';

export type AnimationSpeed = 'slow' | 'normal' | 'fast';
export type AnimationIntensity = 'low' | 'medium' | 'high';

export interface AnimationFields {
  idleAnimation?: IdleAnimation;
  entranceAnimation?: EntranceAnimation;
  animationSpeed?: AnimationSpeed;
  animationIntensity?: AnimationIntensity;
  glowColor?: string;
  shakeIntervalSeconds?: number;
}

function speedMultiplier(s: AnimationSpeed | undefined): number {
  if (s === 'fast') return 0.5;
  if (s === 'slow') return 2;
  return 1;
}

function intensityMultiplier(i: AnimationIntensity | undefined): number {
  if (i === 'low') return 0.5;
  if (i === 'high') return 2;
  return 1;
}

/**
 * Returns class names + the CSS variable bag that `animations.css` reads.
 * Caller spreads `style` onto the root and assigns `className`.
 */
export function buildAnimationClasses(fields: AnimationFields): {
  className: string;
  style: React.CSSProperties;
} {
  const idle = (fields.idleAnimation ?? 'none') as IdleAnimation;
  const entrance = (fields.entranceAnimation ?? 'none') as EntranceAnimation;

  const classes = ['hv-anim-root'];
  if (entrance !== 'none') classes.push(`hv-entrance-${entrance}`);
  if (idle !== 'none') classes.push(`hv-idle-${idle}`);

  const style: React.CSSProperties & Record<string, string | number> = {
    ['--hv-anim-speed']: String(speedMultiplier(fields.animationSpeed)),
    ['--hv-anim-intensity']: String(intensityMultiplier(fields.animationIntensity)),
  };
  if (idle === 'neon-glow') {
    style['--hv-glow-color'] = fields.glowColor ?? '#ffffff';
  }
  if (idle === 'shake-interval') {
    const n = Math.max(1, Math.min(60, Math.floor(fields.shakeIntervalSeconds ?? 5)));
    style['--hv-shake-interval'] = `${n}s`;
  }
  return { className: classes.join(' '), style };
}

/** Whether the idle animation is wave (chars get split into spans). */
export function isWaveIdle(fields: AnimationFields): boolean {
  return (fields.idleAnimation ?? 'none') === 'wave';
}

/** Whether the entrance is typewriter (text widget runs a per-char width animation). */
export function isTypewriterEntrance(fields: AnimationFields): boolean {
  return (fields.entranceAnimation ?? 'none') === 'typewriter';
}
